import React, { useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import Card, { CardBody, CardHeader } from '../ui/Card';
import { Badge } from '../ui/Badge';
import Button from '../ui/Button';
import ClassificationBadges from './ClassificationBadges';
import ExecutiveSummarySection from './ExecutiveSummarySection';
import KeyInsightsList from './KeyInsightsList';
import RecommendationsList from './RecommendationsList';
import DataQualityIndicator from './DataQualityIndicator';
import WorkflowExecutionDetails from './WorkflowExecutionDetails';
import { ConsultingAnalysisPanel } from './consulting';
import type { QuestionAnalysisResult } from '../../types/workflowAnalysis';

interface StrategicQuestionAnalysisCardProps {
  result: QuestionAnalysisResult;
  expanded?: boolean;
  className?: string;
  onReanalyze?: (questionId: string) => Promise<void>;
  isReanalyzing?: boolean;
}

export const StrategicQuestionAnalysisCard: React.FC<StrategicQuestionAnalysisCardProps> = ({
  result,
  expanded = false,
  className = '',
  onReanalyze,
  isReanalyzing = false
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);

  const hasWorkflowResult = result.has_workflow_result && result.workflow_execution;
  const hasConsultingAnalysis = !!result.consulting_analysis;
  const hasExistingAnalysis = hasWorkflowResult || hasConsultingAnalysis;
  const statusColor = result.status === 'completed' ? 'success' : result.status === 'failed' ? 'error' : 'warning';

  const handleReanalyze = async () => {
    if (onReanalyze && result.question_id) {
      await onReanalyze(result.question_id);
    }
  };

  return (
    <Card className={`${className}`}>
      <CardHeader>
        <div className="space-y-3">
          {/* Question Text */}
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-lg font-semibold text-neutral-900 flex-1">
              {result.question_text}
            </h3>
            <div className="flex items-center gap-2">
              {/* Re-analyze Button */}
              {onReanalyze && hasExistingAnalysis && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReanalyze}
                  disabled={isReanalyzing}
                  className="flex items-center gap-1.5"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isReanalyzing ? 'animate-spin' : ''}`} />
                  {isReanalyzing ? 'Re-analyzing...' : 'Re-analyze'}
                </Button>
              )}
              <Badge variant={statusColor}>
                {result.status}
              </Badge>
            </div>
          </div>

          {/* Classification Badges */}
          {result.classification && (
            <ClassificationBadges classification={result.classification} />
          )}

          {/* Expand/Collapse Button */}
          {(hasWorkflowResult || hasConsultingAnalysis) && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide analysis details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  View full analysis
                </>
              )}
            </button>
          )}
        </div>
      </CardHeader>

      {/* Expanded Content */}
      {isExpanded && (hasWorkflowResult || hasConsultingAnalysis) && (
        <CardBody className="border-t">
          <div className="space-y-6">
            {/* Amplinth Analysis Panel - Primary content */}
            {hasConsultingAnalysis && result.consulting_analysis && (
              <ConsultingAnalysisPanel
                analysis={result.consulting_analysis}
                defaultExpanded={true}
              />
            )}

            {/* Workflow execution metadata (only if no consulting analysis) */}
            {hasWorkflowResult && !hasConsultingAnalysis && result.workflow_execution && (
              <>
                {result.workflow_execution.executive_summary && (
                  <ExecutiveSummarySection
                    summary={result.workflow_execution.executive_summary}
                  />
                )}
              </>
            )}
          </div>
        </CardBody>
      )}

      {/* Failed State */}
      {result.status === 'failed' && result.error && (
        <CardBody className="border-t bg-red-50">
          <div className="text-sm text-red-800">
            <p className="font-medium">Analysis Failed</p>
            <p className="mt-1 text-red-700">{result.error}</p>
          </div>
        </CardBody>
      )}

      {/* No Workflow Result */}
      {!hasWorkflowResult && result.status === 'completed' && (
        <CardBody className="border-t bg-amber-50">
          <p className="text-sm text-amber-800">
            Analysis completed but no workflow results available.
          </p>
        </CardBody>
      )}
    </Card>
  );
};

export default StrategicQuestionAnalysisCard;
